/**
 * Chat BI postMessage 通讯协议
 *
 * 所有消息都携带 `source: 'chat-bi'` 标识，用于过滤非本应用的消息。
 * 父 → 子 的消息类型以 `Inbound` 前缀开头，子 → 父 以 `Outbound` 前缀开头。
 */

import { ICommon } from "./chat";

/** 消息方向标识 */
export const BRIDGE_SOURCE = 'chat-bi' as const;

// ─── 父页面 → Chat BI（Inbound） ───────────────────────────

/** 父页面传入初始化配置 */
export interface InboundInit {
  type: 'init';
  payload: {
    /** 认证 token */
    token?: ICommon['token'];
    /** 语言标识 */
    language?: ICommon['language'];
    /** 租户编码 */
    tenantCode?: ICommon['tenantCode'];
    /** 扩展会话字段，合并进经营分析请求的 extraSession */
    extraSession?: ICommon['extraSession'];
    /** 店铺id */
    storeId?: ICommon['storeId'];
    /** 组织id */
    orgId?: ICommon['orgId'];
    /** 业务类型 */
    bizType?: ICommon['bizType'];
    /** 解决方案编码 */
    solution_code?: ICommon['solution_code'];
    /** GA 上报端，或 Mind（输入框 @ 引用、/ 选择技能，依赖 window.MindHost） */
    source?: ICommon['source'];
    /** 门店时区，如 Asia/Bangkok。目前仅用于转发给门店AI营销页面 */
    timezone?: ICommon['timezone'];
    /** 门店币种，如 THB。目前仅用于转发给门店AI营销页面 */
    currency?: ICommon['currency'];
    /** 商户身份角色，如 merchant_owner。目前仅用于转发给门店AI营销页面 */
    roles?: ICommon['roles'];

    email?: string;
    uid?: string;
    phone?: string;
    /** 配置 */
    config?: {
      theme?: 'light' | 'dark'; // 预留主题配置项，当前版本未使用
      close?: boolean; // sdk和model模式中使用该属性，iframe中通过postMessage通知父页面关闭面板
      fullScreen?: boolean; // sdk和model模式中使用该属性，iframe中通过postMessage通知父页面全屏面板
      /** iframe 模式：为 true 时才显示新建聊天 / 历史记录按钮 */
      history?: boolean;
      onClose?: () => void;
      onFullScreen?: () => void;
    };
  };
}


/** 父页面指令：关闭/展开面板（通知 Chat BI 可见性变化） */
export interface InboundSetVisible {
  type: 'set-visible';
  payload: {
    visible: boolean;
  };
}

/** 父页面 → Chat BI 所有消息类型 */
export type InboundMessage = InboundInit | InboundSetVisible;

// ─── Chat BI → 父页面（Outbound） ──────────────────────────

/** Chat BI 就绪通知 */
export interface OutboundReady {
  type: 'ready';
}

/** 用户发送了消息 */
export interface OutboundUserMessage {
  type: 'user-message';
  payload: {
    text: string;
    sessionId: string;
  };
}

/** AI 回复完成 */
export interface OutboundAiReply {
  type: 'ai-reply';
  payload: {
    text: string;
    sessionId: string;
    messageId: string;
  };
}

/** 用户请求关闭面板 */
export interface OutboundClose {
  type: 'close';
}

/** AI 回复状态变化 */
export interface OutboundStreamingChange {
  type: 'streaming-change';
  payload: {
    streaming: boolean;
  };
}

/** 面板内部错误 */
export interface OutboundError {
  type: 'error';
  payload: {
    code: string;
    message: string;
  };
}

export interface OutboundFullScreen {
  type: 'full-screen';
}

/** widget 模式：请求宿主放大 iframe 以展示 Drawer */
export interface OutboundWidgetOpen {
  type: 'widget-open';
}

/** widget 模式：请求宿主缩小 iframe 仅保留悬浮入口 */
export interface OutboundWidgetClose {
  type: 'widget-close';
}

/** Chat BI → 父页面 所有消息类型 */
export type OutboundMessage =
  | OutboundReady
  | OutboundClose
  | OutboundFullScreen
  | OutboundError
  | OutboundWidgetOpen
  | OutboundWidgetClose;

// ─── 通用信封 ───────────────────────────────────────────────

/** 实际传输的消息信封（包含 source 标识） */
export interface BridgeEnvelope<T = InboundMessage | OutboundMessage> {
  source: typeof BRIDGE_SOURCE;
  data: T;
}
